import {skipToken} from '@reduxjs/toolkit/query';
import {useLocale} from 'platform/locale';

import {isNil, isNotNil} from 'ramda';

import {useAddrDetailQuery, useAddrSuggestionsQuery} from '../store/addressApi';
import {FilterFormAddress} from '../types/FilterFormData';
import {PriceReportType} from '../types/PriceReportType';

type UseResolvedDefaultAddressResult = {
  /** Report address with coordinates, or null when it (yet) cannot be resolved. */
  resolvedAddress: FilterFormAddress | null;
  /** True while the suggestion → place-detail chain may still deliver coordinates. */
  isResolving: boolean;
};

/**
 * Resolves the price report's default address into coordinates via the address
 * suggestion + place detail queries — independently of the filter form. Used where
 * filters resolve against the default form values (the dashboard saved-filters
 * card): without coordinates a preset radius could not be applied at all.
 * The underlying RTK Query cache is shared with the filter form's own lookup.
 */
export function useResolvedDefaultAddress(
  priceReport: PriceReportType
): UseResolvedDefaultAddressResult {
  const {language} = useLocale();
  const priceReportAddress = priceReport.vehicle.location?.address ?? null;

  const {
    data: suggestions,
    isFetching: isSuggestionsFetching,
    isError: isSuggestionsError,
  } = useAddrSuggestionsQuery(
    isNotNil(priceReportAddress)
      ? {term: priceReportAddress, country: priceReport.market, language}
      : skipToken
  );
  const firstSuggestion = suggestions?.find(
    (suggestion) => typeof suggestion?.placeId === 'string'
  );
  const placeId = firstSuggestion?.placeId;
  const hasPlaceId = typeof placeId === 'string';

  const {
    data: addressDetail,
    isFetching: isDetailFetching,
    isError: isDetailError,
  } = useAddrDetailQuery(
    isNotNil(priceReportAddress) && typeof placeId === 'string' ? {placeId, language} : skipToken
  );

  if (isNil(priceReportAddress)) {
    return {resolvedAddress: null, isResolving: false};
  }

  const lat = parseFloat(addressDetail?.gps?.lat ?? '');
  const lng = parseFloat(addressDetail?.gps?.lng ?? '');
  const resolvedAddress: FilterFormAddress | null =
    Number.isFinite(lat) && Number.isFinite(lng)
      ? {label: firstSuggestion?.formattedAddress ?? priceReportAddress, lat, lng}
      : null;

  const isSuggestionsPending = isNil(suggestions) && !isSuggestionsError;
  const isDetailPending = hasPlaceId && isNil(addressDetail) && !isDetailError;
  const isResolving =
    isNil(resolvedAddress) &&
    (isSuggestionsPending || isSuggestionsFetching || isDetailPending || isDetailFetching);

  return {resolvedAddress, isResolving};
}
